package com.snakesworld.game

import androidx.compose.animation.ContentTransform
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.runtime.Composable
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.ui.NavDisplay
import androidx.navigation3.ui.defaultPredictivePopTransitionSpec
import com.snakesworld.game.audio.SoundManager
import com.snakesworld.game.di.appModule
import com.snakesworld.game.navigation.AppSavedStateConfiguration
import com.snakesworld.game.navigation.ConnectingRoute
import com.snakesworld.game.navigation.HubRoute
import com.snakesworld.game.navigation.InGameRoute
import com.snakesworld.game.navigation.LeaderboardRoute
import com.snakesworld.game.navigation.MainMenuRoute
import com.snakesworld.game.navigation.MatchSystemBackPolicy
import com.snakesworld.game.navigation.SettingsRoute
import com.snakesworld.game.navigation.SinglePlayerMenuRoute
import com.snakesworld.game.navigation.SplashRoute
import com.snakesworld.game.network.newUserId
import com.snakesworld.game.navigation.shouldBlockPlatformBack
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.ui.ConnectingScreen
import com.snakesworld.game.ui.GameScreen
import com.snakesworld.game.ui.HubScreen
import com.snakesworld.game.ui.LeaderboardScreen
import com.snakesworld.game.ui.MainMenuScreen
import com.snakesworld.game.ui.SettingsScreen
import com.snakesworld.game.ui.SplashScreen
import com.snakesworld.game.ui.theme.GameTheme
import com.snakesworld.game.ui.theme.JoystickType
import com.snakesworld.game.ui.theme.LocalJoystickType
import com.snakesworld.game.viewmodel.AuthViewModel
import com.snakesworld.game.viewmodel.ConnectingViewModel
import com.snakesworld.game.viewmodel.GameViewModel
import com.snakesworld.game.viewmodel.LeaderboardViewModel
import com.snakesworld.game.viewmodel.MenuViewModel
import org.koin.compose.KoinApplication
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.dsl.koinConfiguration

@Composable
fun App() {
    KoinApplication(
        configuration = koinConfiguration(declaration = { modules(appModule) }),
        content = {
            val joystickTypeState = rememberSaveable { mutableStateOf(JoystickType.Joystick) }

            CompositionLocalProvider(
                LocalJoystickType provides joystickTypeState
            ) {
                GameTheme {
                    // Pop with removeAt(lastIndex), never List.removeLast / removeFirst /
                    // getFirst / getLast. Those are Java 21 SequencedCollection methods
                    // (Android API 35+ only) and crash with NoSuchMethodError on minSdk 24.
                    // removeLastOrNull() is fine: it is a stdlib-only extension.
                    val backStack = rememberNavBackStack(AppSavedStateConfiguration, SplashRoute)
                    val menuViewModel: MenuViewModel = koinViewModel()

                    MatchSystemBackPolicy(
                        blockSystemBack = shouldBlockPlatformBack(backStack.lastOrNull()),
                    ) {
                    NavDisplay(
                        backStack = backStack,
                        // Safety net if NavDisplay still owns the gesture: do not pop a match,
                        // and do not slide the previous screen in while the finger is down.
                        onBack = {
                            if (!shouldBlockPlatformBack(backStack.lastOrNull())) {
                                backStack.removeLastOrNull()
                            }
                        },
                        predictivePopTransitionSpec = { edge ->
                            if (shouldBlockPlatformBack(backStack.lastOrNull())) {
                                ContentTransform(
                                    targetContentEnter = EnterTransition.None,
                                    initialContentExit = ExitTransition.None,
                                )
                            } else {
                                defaultPredictivePopTransitionSpec<NavKey>().invoke(this, edge)
                            }
                        },
                        entryProvider = entryProvider {
                            entry<SplashRoute> {
                                val authViewModel: AuthViewModel = koinViewModel()
                                SplashScreen(
                                    viewModel = authViewModel,
                                    onAuthenticated = {
                                        if (backStack.isNotEmpty() && backStack.last() is SplashRoute) {
                                            backStack.removeAt(backStack.lastIndex)
                                            backStack.add(HubRoute)
                                        }
                                    }
                                )
                            }
                            entry<HubRoute> {
                                HubScreen(
                                    viewModel = menuViewModel,
                                    onSinglePlayer = {
                                        backStack.add(SinglePlayerMenuRoute)
                                    },
                                    onMultiplayer = {
                                        backStack.add(MainMenuRoute)
                                    },
                                    onSettings = {
                                        backStack.add(SettingsRoute)
                                    }
                                )
                            }
                            entry<SinglePlayerMenuRoute> {
                                MainMenuScreen(
                                    viewModel = menuViewModel,
                                    isSinglePlayerMode = true,
                                    onStartGame = { userId, callsign, skinId ->
                                        val uid = userId.ifBlank { menuViewModel.uiState.value.profile.uid }
                                            .ifBlank { "offline_${newUserId()}" }
                                        val nick = callsign.ifBlank { "PLAYER_01" }
                                        backStack.add(
                                            InGameRoute(
                                                assignedId = uid,
                                                nickname = nick,
                                                skinId = skinId,
                                                isSinglePlayer = true,
                                            )
                                        )
                                    },
                                    onNavigateLeaderboard = { /* SP history is inline; no global board */ },
                                    onBackToHub = {
                                        if (backStack.isNotEmpty() && backStack.last() is SinglePlayerMenuRoute) {
                                            backStack.removeAt(backStack.lastIndex)
                                        }
                                    }
                                )
                            }
                            entry<MainMenuRoute> {
                                MainMenuScreen(
                                    viewModel = menuViewModel,
                                    isSinglePlayerMode = false,
                                    onStartGame = { userId, callsign, skinId ->
                                        backStack.add(ConnectingRoute(userId, callsign, skinId))
                                    },
                                    onNavigateLeaderboard = {
                                        backStack.add(LeaderboardRoute)
                                    },
                                    onBackToHub = {
                                        if (backStack.isNotEmpty() && backStack.last() is MainMenuRoute) {
                                            backStack.removeAt(backStack.lastIndex)
                                        }
                                    }
                                )
                            }
                            entry<ConnectingRoute> { route ->
                                val connectingViewModel: ConnectingViewModel = koinViewModel()
                                ConnectingScreen(
                                    viewModel = connectingViewModel,
                                    userId = route.userId,
                                    callsign = route.nickname,
                                    skinId = route.skinId,
                                    onConnected = { assignedId ->
                                        if (backStack.isNotEmpty() && backStack.last() is ConnectingRoute) {
                                            backStack.removeAt(backStack.lastIndex)
                                        }
                                        backStack.add(
                                            InGameRoute(
                                                assignedId,
                                                route.nickname,
                                                route.skinId,
                                                isSinglePlayer = false,
                                            )
                                        )
                                    },
                                    onBackToMenu = {
                                        if (backStack.isNotEmpty() && backStack.last() is ConnectingRoute) {
                                            backStack.removeAt(backStack.lastIndex)
                                        }
                                    }
                                )
                            }
                            entry<InGameRoute> { route ->
                                val gameViewModel: GameViewModel = koinViewModel()
                                val menuState = menuViewModel.uiState.collectAsStateWithLifecycle()
                                GameScreen(
                                    viewModel = gameViewModel,
                                    assignedId = route.assignedId,
                                    nickname = route.nickname,
                                    skinId = route.skinId,
                                    isSinglePlayer = route.isSinglePlayer,
                                    blockedPlayerIds = menuState.value.profile.blockedIds.toSet(),
                                    onReportPlayer = { id, name -> menuViewModel.reportPlayer(id, name) },
                                    onBlockPlayer = { id -> menuViewModel.blockPlayer(id) },
                                    onExitMatch = {
                                        if (route.isSinglePlayer) {
                                            while (backStack.isNotEmpty() && backStack.last() !is SinglePlayerMenuRoute) {
                                                if (backStack.last() is HubRoute) break
                                                backStack.removeAt(backStack.lastIndex)
                                            }
                                            if (backStack.none { it is SinglePlayerMenuRoute }) {
                                                // Hub should still be under us; open SP menu again so stats reload.
                                                if (backStack.none { it is HubRoute }) {
                                                    backStack.add(HubRoute)
                                                }
                                                backStack.add(SinglePlayerMenuRoute)
                                            }
                                        } else {
                                            while (backStack.size > 1 && backStack.last() !is HubRoute) {
                                                backStack.removeAt(backStack.lastIndex)
                                            }
                                            if (backStack.none { it is HubRoute }) {
                                                backStack.add(HubRoute)
                                            }
                                        }
                                    }
                                )
                            }
                            entry<LeaderboardRoute> {
                                val leaderboardViewModel: LeaderboardViewModel = koinViewModel()
                                val menuState = menuViewModel.uiState.collectAsStateWithLifecycle()
                                LeaderboardScreen(
                                    viewModel = leaderboardViewModel,
                                    currentUserId = menuState.value.profile.uid,
                                    blockedPlayerIds = menuState.value.profile.blockedIds.toSet(),
                                    onReportPlayer = { id, name -> menuViewModel.reportPlayer(id, name) },
                                    onBlockPlayer = { id -> menuViewModel.blockPlayer(id) },
                                    onBackToMenu = {
                                        backStack.removeAt(backStack.lastIndex)
                                    }
                                )
                            }
                            entry<SettingsRoute> {
                                val soundManager: SoundManager = koinInject()
                                val repository: MultiplayerRepository = koinInject()
                                SettingsScreen(
                                    soundManager = soundManager,
                                    repository = repository,
                                    onUserDataCleared = {
                                        menuViewModel.resetUserData()
                                        while (backStack.isNotEmpty()) {
                                            backStack.removeAt(backStack.lastIndex)
                                        }
                                        backStack.add(SplashRoute)
                                    },
                                    onBackToMenu = {
                                        backStack.removeAt(backStack.lastIndex)
                                    }
                                )
                            }
                        }
                    )
                    }
                }
            }
        })
}